import React, { useState } from "react";
import Header from "../components/Header";
import DateRangePicker from "../components/DateRangePicker";
import DataCard from "../components/DataCard";
import { useReport } from "../hooks/useReport";

const Report = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportType, setReportType] = useState("summary");

  const {
    loading,
    sellReport,
    purchaseReport,
    summaryReport,
    generateSellReport,
    generatePurchaseReport,
    generateSummaryReport,
  } = useReport();

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    switch (reportType) {
      case "sell":
        await generateSellReport(startDate, endDate);
        break;
      case "purchase":
        await generatePurchaseReport(startDate, endDate);
        break;
      case "summary":
        await generateSummaryReport(startDate, endDate);
        break;
      default:
        break;
    }
  };

  const renderSellReport = () => {
    if (!sellReport) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DataCard
            title="Total Sales"
            value={`₹${sellReport.totalSales?.toFixed(2) || "0.00"}`}
            icon="fas fa-shopping-cart"
            color="green"
          />
          <DataCard
            title="Total Orders"
            value={sellReport.totalOrders || 0}
            icon="fas fa-receipt"
            color="blue"
          />
          <DataCard
            title="Average Order Value"
            value={`₹${sellReport.averageOrderValue?.toFixed(2) || "0.00"}`}
            icon="fas fa-chart-bar"
            color="purple"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800 border border-blue-200 rounded-lg overflow-hidden">
            <thead className="bg-gradient-to-r from-blue-200 via-indigo-100 to-teal-100 text-blue-900 uppercase text-xs tracking-wide">
              <tr>
                <th className="py-3 px-4 text-left">Order ID</th>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Customer</th>
                <th className="py-3 px-4 text-left">Items</th>
                <th className="py-3 px-4 text-left">Amount</th>
              </tr>
            </thead>
            <tbody>
              {sellReport.orders?.map((order, index) => (
                <tr
                  key={index}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-blue-50"
                  } hover:bg-indigo-50 transition-colors`}
                >
                  <td className="py-3 px-4">{order.orderId}</td>
                  <td className="py-3 px-4">{order.date}</td>
                  <td className="py-3 px-4">{order.customerName}</td>
                  <td className="py-3 px-4">{order.itemCount}</td>
                  <td className="py-3 px-4 font-semibold text-green-600">
                    ₹{order.amount?.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPurchaseReport = () => {
    if (!purchaseReport) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DataCard
            title="Total Purchases"
            value={`₹${purchaseReport.totalPurchases?.toFixed(2) || "0.00"}`}
            icon="fas fa-shopping-bag"
            color="orange"
          />
          <DataCard
            title="Total Items"
            value={purchaseReport.totalItems || 0}
            icon="fas fa-boxes"
            color="indigo"
          />
          <DataCard
            title="Average Purchase"
            value={`₹${purchaseReport.averagePurchase?.toFixed(2) || "0.00"}`}
            icon="fas fa-chart-line"
            color="teal"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800 border border-blue-200 rounded-lg overflow-hidden">
            <thead className="bg-gradient-to-r from-blue-200 via-indigo-100 to-teal-100 text-blue-900 uppercase text-xs tracking-wide">
              <tr>
                <th className="py-3 px-4 text-left">Medicine Name</th>
                <th className="py-3 px-4 text-left">Quantity</th>
                <th className="py-3 px-4 text-left">Purchase Price</th>
                <th className="py-3 px-4 text-left">Total Cost</th>
                <th className="py-3 px-4 text-left">Supplier</th>
              </tr>
            </thead>
            <tbody>
              {purchaseReport.purchases?.map((purchase, index) => (
                <tr
                  key={index}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-blue-50"
                  } hover:bg-indigo-50 transition-colors`}
                >
                  <td className="py-3 px-4 font-medium">{purchase.name}</td>
                  <td className="py-3 px-4">{purchase.quantity}</td>
                  <td className="py-3 px-4">₹{purchase.purchasePrice?.toFixed(2)}</td>
                  <td className="py-3 px-4 font-semibold text-orange-600">
                    ₹{purchase.totalCost?.toFixed(2)}
                  </td>
                  <td className="py-3 px-4">{purchase.supplier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSummaryReport = () => {
    if (!summaryReport) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DataCard
            title="Total Revenue"
            value={`₹${summaryReport.totalRevenue?.toFixed(2) || "0.00"}`}
            icon="fas fa-rupee-sign"
            color="green"
          />
          <DataCard
            title="Total Expenses"
            value={`₹${summaryReport.totalExpenses?.toFixed(2) || "0.00"}`}
            icon="fas fa-money-bill-wave"
            color="red"
          />
          <DataCard
            title="Net Profit"
            value={`₹${summaryReport.netProfit?.toFixed(2) || "0.00"}`}
            icon="fas fa-chart-line"
            color="blue"
          />
          <DataCard
            title="Profit Margin"
            value={`${summaryReport.profitMargin?.toFixed(2) || "0"}%`}
            icon="fas fa-percentage"
            color="purple"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md border border-blue-200 p-6">
            <h3 className="text-lg font-bold text-blue-800 mb-4">
              Top Selling Medicines
            </h3>
            <div className="space-y-3">
              {summaryReport.topSellingMedicines?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-blue-50 rounded-lg"
                >
                  <span className="font-medium">{item.name}</span>
                  <span className="text-green-600 font-semibold">
                    {item.quantity} units
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-blue-200 p-6">
            <h3 className="text-lg font-bold text-blue-800 mb-4">
              Low Stock Alert
            </h3>
            <div className="space-y-3">
              {summaryReport.lowStockItems?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-red-50 rounded-lg"
                >
                  <span className="font-medium">{item.name}</span>
                  <span className="text-red-600 font-semibold">
                    {item.quantity} left
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-6">
      <Header showSearch={false} showCart={false} />

      <div className="flex-1 overflow-y-auto mt-6 space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md border border-blue-200 p-6">
          <div className="flex items-center gap-3">
            <i className="fas fa-file-alt text-3xl text-blue-600"></i>
            <div>
              <h1 className="text-2xl font-bold text-blue-800">Reports</h1>
              <p className="text-sm text-gray-600">
                Generate and view sell, purchase, and summary reports
              </p>
            </div>
          </div>
        </div>

        {/* Report Display */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-blue-200 hover:border-indigo-300 transition">
          <div className="flex items-center gap-2 text-blue-700 mb-4">
            <i className="fas fa-file-alt text-xl"></i>
            <h2 className="text-xl font-semibold">Report Type</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                Report Type:
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm bg-white"
              >
                <option value="summary">Summary Report</option>
                <option value="sell">Sell Report</option>
                <option value="purchase">Purchase Report</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                From Date:
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm"
              />
            </div>

            <div className="flex items-center">
              <label className="w-32 text-sm font-medium text-gray-800">
                To Date:
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1 w-40 rounded border border-blue-300 focus:ring focus:ring-blue-300 focus:outline-none text-sm"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              className={`flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-indigo-500 text-white font-semibold px-6 py-2 rounded-full shadow-md transition duration-200 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Generating...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Generate Report
                </>
              )}
            </button>
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="text-center py-12">
                <i className="fas fa-spinner fa-spin text-blue-500 text-4xl"></i>
                <p className="mt-4 text-gray-600">Generating report...</p>
              </div>
            ) : (
              <>
                {reportType === "summary" && renderSummaryReport()}
                {reportType === "sell" && renderSellReport()}
                {reportType === "purchase" && renderPurchaseReport()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
